import { updateSlideInDeck } from "../editor/deckEditor.js";
import { newId } from "../model/surfDeck.js";
import { sanitizeSpeakerNoteMarkdown } from "./speakerMarkdown.js";
import { emptySpeakerScript } from "./scriptOps.js";
export const SPEAKER_NOTE_KINDS = [
    "deepdive",
    "data",
    "objection",
    "anecdote",
    "qa",
    "definition",
    "freeform",
];
function sortNotesForDisplay(notes) {
    const pinned = notes.filter((n) => n.pinned);
    const rest = notes.filter((n) => !n.pinned);
    return [...pinned, ...rest];
}
function withSpeakerNotes(deck, slideId, updater) {
    return updateSlideInDeck(deck, slideId, (slide) => {
        const prev = slide.speakerScript ?? emptySpeakerScript();
        const nextNotes = updater(prev.notes ?? []);
        return {
            ...slide,
            speakerScript: {
                ...prev,
                notes: nextNotes,
                updatedAt: new Date().toISOString(),
            },
        };
    });
}
function isAgentMetadataLine(line) {
    const trimmed = line.trim();
    if (!trimmed)
        return true;
    if (/^[-*]?\s*status\s*:/i.test(trimmed))
        return true;
    if (/^[-*]\s*[a-z][a-z0-9_-]*\s*:/i.test(trimmed))
        return true;
    if (/^[a-z][a-z0-9_-]*\s*:/.test(trimmed))
        return true;
    return false;
}
function cleanTitleLine(line) {
    return line.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
}
function deriveNoteTitle(question, answer) {
    const sanitized = sanitizeSpeakerNoteMarkdown(answer);
    const lines = sanitized.trim().split("\n");
    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line || isAgentMetadataLine(line))
            continue;
        const heading = line.match(/^#{1,3}\s+(.+)$/);
        if (heading) {
            const title = cleanTitleLine(heading[1]);
            if (title && title.length <= 80)
                return title;
            continue;
        }
        const prose = cleanTitleLine(line);
        if (prose && prose.length <= 80)
            return prose;
    }
    const q = question.trim();
    if (q.length <= 80)
        return q;
    return `${q.slice(0, 77)}…`;
}
export function newSpeakerNoteFromAnswer(question, answer, agentName, jobId, kind = "freeform", opts) {
    const now = new Date().toISOString();
    const rawBody = opts?.rawBody ?? sanitizeSpeakerNoteMarkdown(answer);
    const body = opts?.body ?? rawBody;
    const trimmedQuestion = (opts?.question ?? question).trim() || undefined;
    return {
        id: newId(),
        title: deriveNoteTitle(question, body),
        body,
        question: trimmedQuestion,
        rawBody: rawBody !== body ? rawBody : opts?.rawBody,
        summaryStatus: opts?.summaryStatus,
        collapsed: opts?.collapsed ?? true,
        kind,
        source: {
            agentName,
            jobId,
            question: trimmedQuestion,
        },
        pinned: false,
        createdAt: now,
        updatedAt: now,
    };
}
export function getSpeakerNotesForSlide(deck, slideId) {
    const slide = deck.slides.find((s) => s.id === slideId);
    const notes = slide?.speakerScript?.notes ?? [];
    return sortNotesForDisplay(notes);
}
export function addSpeakerNote(deck, slideId, note) {
    return withSpeakerNotes(deck, slideId, (notes) => [...notes, note]);
}
export function updateSpeakerNote(deck, slideId, noteId, patch) {
    return withSpeakerNotes(deck, slideId, (notes) => notes.map((n) => n.id === noteId
        ? { ...n, ...patch, updatedAt: new Date().toISOString() }
        : n));
}
export function removeSpeakerNote(deck, slideId, noteId) {
    return withSpeakerNotes(deck, slideId, (notes) => notes.filter((n) => n.id !== noteId));
}
export function reorderSpeakerNotes(deck, slideId, fromIdx, toIdx) {
    return withSpeakerNotes(deck, slideId, (notes) => {
        if (fromIdx < 0 || fromIdx >= notes.length || toIdx < 0 || toIdx >= notes.length) {
            return notes;
        }
        const next = [...notes];
        const [item] = next.splice(fromIdx, 1);
        if (!item)
            return notes;
        next.splice(toIdx, 0, item);
        return next;
    });
}
export function setSpeakerNoteKind(deck, slideId, noteId, kind) {
    return updateSpeakerNote(deck, slideId, noteId, { kind });
}
export function toggleSpeakerNotePin(deck, slideId, noteId) {
    return withSpeakerNotes(deck, slideId, (notes) => notes.map((n) => n.id === noteId
        ? { ...n, pinned: !n.pinned, updatedAt: new Date().toISOString() }
        : n));
}
export function toggleSpeakerNoteCollapsed(deck, slideId, noteId) {
    return withSpeakerNotes(deck, slideId, (notes) => notes.map((n) => n.id === noteId
        ? {
            ...n,
            collapsed: n.collapsed === false,
            updatedAt: new Date().toISOString(),
        }
        : n));
}
